import {
  FILTER_OPTIONS,
  GAMEWEEK_WINDOW_PRESETS,
  SORT_OPTIONS
} from './useFixtureTickerFilters';
import type { FilterOption, SortOption } from './useFixtureTickerFilters';
import { MAX_GAMEWEEK } from './constants';
import type { TeamWithFixtures } from './constants';
import { TeamFilter } from './TeamFilter';
import SelectBox from '@components/selectbox';
import Toggle from '@components/toggle';
import Slider from '@components/atoms/slider';

type FixtureTickerFiltersProps = {
  selectedFilter: FilterOption;
  onFilterChange: (filter: FilterOption) => void;
  selectedSort: SortOption;
  onSortChange: (sort: SortOption) => void;
  teams: TeamWithFixtures[];
  selectedTeams: string[];
  onSelectedTeamsChange: (next: string[]) => void;
  gameweekStart: number;
  gameweekEnd: number;
  gameweekFloor: number;
  onGameweekRangeChange: (start: number, end: number) => void;
  onGameweekWindowChange: (windowSize: number) => void;
  showTeamStrength: boolean;
  onShowTeamStrengthChange: (next: boolean) => void;
};

const formatGameweekLabel = (gameweek: number) => `GW${gameweek}`;

// The slider renders its pills through BodyRegular (Open Sans 16px), so the
// design's Poppins 600 13px pill with 4px 8px padding and a 12px gap to the
// track has to win with important variants. 54px fits GW38.
const GAMEWEEK_PILL_CLASS_NAME =
  '!font-sans !text-[13px] !font-semibold !leading-5 !py-1 !px-2 !w-auto !min-w-[54px] !mb-0 !rounded-lg !mr-3 !ml-3';

export const FixtureTickerFilters = ({
  selectedFilter,
  onFilterChange,
  selectedSort,
  onSortChange,
  teams,
  selectedTeams,
  onSelectedTeamsChange,
  gameweekStart,
  gameweekEnd,
  gameweekFloor,
  onGameweekRangeChange,
  onGameweekWindowChange,
  showTeamStrength,
  onShowTeamStrengthChange
}: FixtureTickerFiltersProps) => {
  const filterOptions = FILTER_OPTIONS.map((option) => ({
    value: option.id,
    label: option.label
  }));

  const sortOptions = SORT_OPTIONS.map((option) => ({
    value: option.id,
    label: option.label
  }));

  const handleSortChange = (value: string) => {
    const next = SORT_OPTIONS.find((option) => option.id === value);
    if (next) onSortChange(next);
  };

  const selectedWindowSize = gameweekEnd - gameweekStart + 1;

  // Every control sits on one baseline: 14px labels with a 6px gap, 38px
  // controls, bottom-aligned. The selects and team filter have fixed widths
  // and the slider takes the remainder, with a floor that steps up with the
  // viewport. Below 2xl the card is too narrow for all five on one line, so
  // the row wraps and the toggle drops to a second line, right aligned; below
  // lg the controls stack.
  return (
    // relative + z-20 keeps the team dropdown above the table card, whose
    // pinned header cells carry their own stacking context.
    <div
      data-testid="fixture-ticker-filters"
      className="relative z-20 flex flex-col gap-4 rounded-[14px] border border-neutral-200 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)] lg:flex-row lg:flex-wrap lg:items-end 2xl:flex-nowrap"
    >
      <div className="lg:w-[160px] lg:shrink-0">
        <SelectBox
          size="compact"
          name="fixture-ticker-filter"
          label="Sort by"
          value={selectedFilter}
          options={filterOptions}
          onChange={(value) => onFilterChange(value as FilterOption)}
        />
      </div>
      <div className="lg:w-[158px] lg:shrink-0">
        <SelectBox
          size="compact"
          name="fixture-ticker-sort-direction"
          label="Order"
          value={selectedSort.id}
          options={sortOptions}
          onChange={handleSortChange}
        />
      </div>
      <div className="lg:w-[236px] lg:shrink-0">
        <TeamFilter
          teams={teams}
          selectedTeams={selectedTeams}
          onChange={onSelectedTeamsChange}
        />
      </div>
      <div className="min-w-0 flex-1 lg:min-w-[280px] 2xl:min-w-[380px]">
        <div className="mb-1.5 flex items-center justify-between gap-3">
          <label
            htmlFor="fixture-ticker-gw-range"
            className="font-open-sans text-sm text-neutral-800"
          >
            Gameweeks
          </label>
          <span className="flex items-center gap-1.5">
            <span className="font-open-sans text-xs text-neutral-500">
              quick set
            </span>
            {GAMEWEEK_WINDOW_PRESETS.map((windowSize) => {
              const isActive = selectedWindowSize === windowSize;
              return (
                <button
                  key={windowSize}
                  type="button"
                  data-testid={`gameweek-window-${windowSize}`}
                  aria-pressed={isActive}
                  onClick={() => onGameweekWindowChange(windowSize)}
                  className={`h-[22px] min-w-[28px] rounded-md border px-[7px] font-open-sans text-xs font-semibold ${
                    isActive
                      ? 'border-primary-600 bg-primary-600 text-white'
                      : 'border-neutral-300 bg-white text-neutral-700 hover:border-neutral-400'
                  }`}
                >
                  {windowSize}
                </button>
              );
            })}
          </span>
        </div>
        {/* The slider root is itself a flex row in fixedLabels mode and has no
            intrinsic width, so it must be told to fill this wrapper or the
            track collapses to nothing between the pills. The react-slider
            element is only as tall as its 4px track (the thumbs overflow it),
            so it gets the thumb height and the row centres on it. */}
        <div className="flex h-[38px] items-center [&>div]:flex-1 [&>div]:items-center [&_.horizontal-slider]:h-5">
          <Slider
            variant="controlled"
            fixedLabels
            name="fixture-ticker-gw-range"
            min={gameweekFloor}
            max={MAX_GAMEWEEK}
            step={1}
            minStepsBetweenThumbs={0}
            value={[gameweekStart, gameweekEnd]}
            setValue={(value) => onGameweekRangeChange(value[0], value[1])}
            formatLabel={formatGameweekLabel}
            pillClassName={GAMEWEEK_PILL_CLASS_NAME}
          />
        </div>
      </div>
      <div className="lg:ml-auto lg:shrink-0 lg:self-end">
        <Toggle
          name="fixture-ticker-show-strength"
          checked={showTeamStrength}
          onChange={() => onShowTeamStrengthChange(!showTeamStrength)}
          text="Team strength"
        />
      </div>
    </div>
  );
};
